<template>
  <PolicyChangeForm
    :customerType="customerType"
    :title="title"
    :steps="steps"
    :email="email"
    :usingPortalForm="true"
    @navigate="handleNavigation"
    @reset="resetForm"
  >
    <!-- Contact Information Step -->
    <SharedContactInformationStep 
      v-if="currentStep === 1"
      v-model:firstName="firstName"
      v-model:lastName="lastName"
      v-model:policyNumber="policyNumber"
      v-model:phoneNumber="phoneNumber"
      v-model:email="email"
      :customerType="customerType"
      :errors="errors"
      @next="goToNextStep"
    />
    
    <!-- Change Details Step -->
    <template v-if="currentStep === 2">
      <div class="form-section mb-6">
        <h2 class="text-xl font-semibold text-gray-700 mb-4">Change Details</h2>
        
        <div class="mb-4">
          <label
            for="change-type"
            class="block text-responsive-sm font-semibold text-gray-700 mb-2"
          >Type of Change</label>
          <select
            id="change-type"
            v-model="changeType"
            class="input-responsive"
          >
            <option value="">Select change type</option>
            <option value="policy_update">Policy Update</option>
            <option value="coverage_change">Coverage Change</option>
            <option value="personal_info">Personal Information Update</option>
            <option value="other">Other</option>
          </select>
          <p v-if="errors.changeType" class="text-red-500 text-xs italic">
            {{ errors.changeType }}
          </p>
        </div>
        
        <div class="mb-4">
          <label
            for="message"
            class="block text-responsive-sm font-semibold text-gray-700 mb-2"
          >Change Description</label>
          <textarea
            id="message"
            v-model="message"
            rows="4"
            class="input-responsive"
            placeholder="Please describe the changes you need in detail..."
          ></textarea>
          <p v-if="errors.message" class="text-red-500 text-xs italic">
            {{ errors.message }}
          </p>
        </div>
        
        <div class="mb-4">
          <label
            class="block text-responsive-sm font-semibold text-gray-700 mb-2"
          >Supporting Documents</label>
          <DocumentUpload
            v-model="uploadedFile"
            @fileNameChange="handleFileNameChange"
          />
          <p class="text-gray-500 text-xs mt-1">
            Upload any supporting documentation for your request (optional)
          </p>
          <p v-if="errors.uploadedFile" class="text-red-500 text-xs italic">
            {{ errors.uploadedFile }}
          </p>
        </div>
      </div>
      
      <div class="form-navigation flex justify-between mt-6">
        <button
          @click="goToPreviousStep"
          class="btn-secondary"
        >
          Previous
        </button>
        <button
          @click="goToNextStep"
          class="btn-primary"
        >
          Next
        </button>
      </div>
    </template>
    
    <!-- Review and Submit Step -->
    <SharedEffectiveDateStep
      v-if="currentStep === 3"
      v-model:effectiveDate="effectiveDate"
      v-model:comments="comments"
      :customerType="customerType"
      :isSubmitting="isSubmitting"
      :errors="errors"
      @previous="goToPreviousStep"
      @submit="submitForm"
    >
      <template #review-details>
        <div class="review-section mb-6">
          <h3 class="font-semibold text-gray-700 mb-3">Request Details</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-600">Change Type:</p>
              <p class="font-medium">{{ changeTypeDisplay }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600">Supporting Document:</p>
              <p class="font-medium">{{ uploadedFile?.name || 'None' }}</p>
            </div>
            <div class="col-span-1 md:col-span-2">
              <p class="text-sm text-gray-600">Change Description:</p>
              <p class="font-medium">{{ message }}</p>
            </div>
          </div>
        </div>
      </template>
    </SharedEffectiveDateStep>
  </PolicyChangeForm>
</template>

<script>
import { ref, computed } from 'vue';
import PolicyChangeForm from '@/components/common/PolicyChangeForm.vue';
import SharedContactInformationStep from '@/components/customerservice/policy-management/forms/shared/steps/SharedContactInformationStep.vue';
import SharedEffectiveDateStep from '@/components/customerservice/policy-management/forms/shared/steps/SharedEffectiveDateStep.vue';
import DocumentUpload from '@/components/common/DocumentUpload.vue';
import { useOtherPolicyChangeService } from '@/services/policyChangeService';
import { validateEmail, validatePhoneNumber, validatePolicyNumber } from '@/utils/validators';
import { validateEffectiveDate } from '@/utils/policyValidators';
import { getTodayFormatted } from '@/utils/dateUtils';

export default {
  name: 'PolicyOtherChangeForm',
  components: {
    PolicyChangeForm,
    SharedContactInformationStep,
    SharedEffectiveDateStep,
    DocumentUpload
  },
  props: {
    title: {
      type: String,
      default: 'Other Policy Change Request'
    },
    customerType: {
      type: String,
      default: 'customer',
      validator: value => ['customer', 'lender'].includes(value)
    }
  },
  setup(props) {
    const policyChangeService = useOtherPolicyChangeService();
    
    // Form state
    const currentStep = ref(1);
    const formSubmitted = ref(false);
    const isSubmitting = ref(false);
    
    // Form data - contact information
    const firstName = ref('');
    const lastName = ref('');
    const policyNumber = ref('');
    const phoneNumber = ref('');
    const email = ref('');
    
    // Form data - change details
    const changeType = ref('');
    const message = ref('');
    const uploadedFile = ref(null);
    const selectedFileName = ref('');
    
    // Form data - submission details
    const effectiveDate = ref(getTodayFormatted()); // Default to today
    const comments = ref('');
    
    // Form validation
    const errors = ref({});
    
    // Computed properties
    const steps = computed(() => [
      { title: 'Contact Information', number: 1 },
      { title: 'Change Details', number: 2 },
      { title: 'Review and Submit', number: 3 }
    ]);
    
    const changeTypeDisplay = computed(() => {
      const types = {
        'policy_update': 'Policy Update',
        'coverage_change': 'Coverage Change',
        'personal_info': 'Personal Information Update',
        'other': 'Other'
      };
      return types[changeType.value] || 'Not Specified';
    });
    
    // File handling
    const handleFileNameChange = (fileName) => {
      selectedFileName.value = fileName;
    };
    
    // Validation methods
    const validateStep1 = () => {
      errors.value.firstName = !firstName.value ? 'First name is required' : '';
      errors.value.lastName = !lastName.value ? 'Last name is required' : '';
      errors.value.policyNumber = validatePolicyNumber(policyNumber.value);
      errors.value.phoneNumber = validatePhoneNumber(phoneNumber.value);
      errors.value.email = validateEmail(email.value);
      
      return !Object.values(errors.value).some(error => error);
    };
    
    const validateStep2 = () => {
      errors.value.changeType = !changeType.value ? 'Please select a change type' : '';
      errors.value.message = !message.value ? 'Please describe the changes you need' : '';
      
      return !Object.values(errors.value).some(error => error);
    };
    
    const validateStep3 = () => {
      errors.value.effectiveDate = validateEffectiveDate(effectiveDate.value);
      
      return !Object.values(errors.value).some(error => error);
    };
    
    // Navigation methods
    const goToNextStep = () => {
      let isValid = false;
      
      switch (currentStep.value) {
        case 1:
          isValid = validateStep1();
          break;
        case 2:
          isValid = validateStep2();
          break;
        default:
          isValid = true;
      }
      
      if (isValid) {
        currentStep.value += 1;
      }
    };
    
    const goToPreviousStep = () => {
      currentStep.value -= 1;
    };
    
    const handleNavigation = (step) => {
      currentStep.value = step;
    };
    
    // Form submission
    const submitForm = async () => {
      if (!validateStep3()) {
        return;
      }
      
      isSubmitting.value = true;
      
      try {
        const response = await policyChangeService.submitOtherPolicyChange({
          contactInfo: {
            firstName: firstName.value,
            lastName: lastName.value,
            policyNumber: policyNumber.value,
            phoneNumber: phoneNumber.value,
            email: email.value
          },
          changeDetails: {
            changeType: changeType.value,
            description: message.value,
            file: uploadedFile.value
          },
          submissionDetails: {
            effectiveDate: effectiveDate.value,
            comments: comments.value
          }
        });
        
        formSubmitted.value = true;
      } catch (error) {
        console.error('Error submitting form:', error);
      } finally {
        isSubmitting.value = false;
      }
    };
    
    // Reset form to initial state
    const resetForm = () => {
      firstName.value = '';
      lastName.value = '';
      policyNumber.value = '';
      phoneNumber.value = '';
      email.value = '';
      changeType.value = '';
      message.value = '';
      uploadedFile.value = null;
      selectedFileName.value = '';
      effectiveDate.value = getTodayFormatted();
      comments.value = '';
      errors.value = {};
      currentStep.value = 1;
      formSubmitted.value = false;
    };
    
    return {
      // Form state
      currentStep,
      formSubmitted,
      isSubmitting,
      
      // Form data
      firstName,
      lastName,
      policyNumber,
      phoneNumber,
      email,
      changeType,
      changeTypeDisplay,
      message,
      uploadedFile,
      selectedFileName,
      effectiveDate,
      comments,
      
      // Errors
      errors,
      
      // Methods
      handleFileNameChange,
      goToNextStep,
      goToPreviousStep,
      handleNavigation,
      submitForm,
      resetForm
    };
  }
}
</script> 